/* eslint-disable */
/*
 * Invoice Builder print runtime.
 *
 * The source of `zpRuntime` is embedded verbatim into every saved print
 * template (see ../compile.js), and the backend only injects the document
 * data. So this function must stay fully self-contained:
 *   - no imports and no references to anything outside its own body
 *   - plain ES5 only (no spread, for..of, classes, destructuring, async,
 *     optional chaining), so Babel never injects helpers living outside it.
 *
 * It is used in two places:
 *   1. the builder screen (live WYSIWYG canvas + formula previews)
 *   2. the printed page (renders the template with the injected data)
 */
export function zpRuntime() {
  "use strict";

  var VERSION = 1;
  var MM_TO_PX = 96 / 25.4;
  var hasOwn = Object.prototype.hasOwnProperty;

  /* ------------------------------------------------------------------ *
   * Small utilities
   * ------------------------------------------------------------------ */
  function isArray(v) {
    return Object.prototype.toString.call(v) === "[object Array]";
  }
  function isBlank(v) {
    return v === null || v === undefined || v === "" || (typeof v === "number" && isNaN(v));
  }
  var NUM_RE = /^\s*[-+]?(\d{1,3}(,\d{2,3})+|\d*)(\.\d+)?\s*$/;
  function isNumeric(v) {
    if (typeof v === "number") return !isNaN(v);
    if (typeof v === "boolean") return true;
    if (typeof v === "string") return v.trim() !== "" && NUM_RE.test(v) && /\d/.test(v);
    return false;
  }
  function toNum(v) {
    if (typeof v === "number") return isNaN(v) ? 0 : v;
    if (typeof v === "boolean") return v ? 1 : 0;
    if (typeof v === "string") {
      var s = v.replace(/,/g, "").trim();
      if (s === "") return 0;
      var n = parseFloat(s);
      return isNaN(n) ? 0 : n;
    }
    if (isArray(v)) {
      var t = 0;
      for (var i = 0; i < v.length; i++) t += toNum(v[i]);
      return t;
    }
    return 0;
  }
  function toStr(v) {
    if (v === null || v === undefined) return "";
    if (typeof v === "number") return autoNumber(v);
    if (isArray(v)) {
      var out = [];
      for (var i = 0; i < v.length; i++) if (!isBlank(v[i])) out.push(toStr(v[i]));
      return out.join(", ");
    }
    if (v instanceof Date) return formatDate(v, "DD/MM/YYYY");
    if (typeof v === "object") {
      try {
        return JSON.stringify(v);
      } catch (e) {
        return "";
      }
    }
    return String(v);
  }
  function truthy(v) {
    if (isArray(v)) return v.length > 0;
    if (typeof v === "string") {
      var s = v.trim().toLowerCase();
      if (s === "" || s === "false" || s === "0" || s === "no" || s === "null") return false;
      return true;
    }
    return !!v;
  }
  function autoNumber(n) {
    if (!isFinite(n)) return "";
    if (Math.round(n) === n) return String(n);
    return String(parseFloat(n.toFixed(6)));
  }
  function round(n, d) {
    d = d || 0;
    var f = Math.pow(10, d);
    var x = n * f;
    // nudge so 1.005 -> 1.01 like a human expects
    return Math.round(x + (x >= 0 ? 1e-9 : -1e-9)) / f;
  }
  function escHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
  function cssVal(v) {
    return String(v == null ? "" : v).replace(/[<>{};"\\]/g, "");
  }
  function num(v, d) {
    var n = parseFloat(v);
    return isNaN(n) ? d : n;
  }
  function mm(v) {
    return round(num(v, 0), 3) + "mm";
  }
  function extend(a, b) {
    var o = {};
    var k;
    if (a) for (k in a) if (hasOwn.call(a, k)) o[k] = a[k];
    if (b) for (k in b) if (hasOwn.call(b, k)) o[k] = b[k];
    return o;
  }

  /* ------------------------------------------------------------------ *
   * Dates
   * ------------------------------------------------------------------ */
  var MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  var DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  function parseDate(v) {
    if (isBlank(v)) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v === "number") return new Date(v);
    var s = String(v).trim();
    var m = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})(?:[ ,T]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?$/.exec(s);
    if (m) {
      var y = parseInt(m[3], 10);
      if (y < 100) y += 2000;
      var h = m[4] ? parseInt(m[4], 10) : 0;
      if (m[7]) {
        var pm = m[7].toLowerCase() === "pm";
        if (pm && h < 12) h += 12;
        if (!pm && h === 12) h = 0;
      }
      return new Date(y, parseInt(m[2], 10) - 1, parseInt(m[1], 10), h, m[5] ? parseInt(m[5], 10) : 0, m[6] ? parseInt(m[6], 10) : 0);
    }
    if (/^\d{10,13}$/.test(s)) return new Date(parseInt(s, 10) * (s.length === 10 ? 1000 : 1));
    var d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  function pad(n, l) {
    var s = String(n);
    while (s.length < (l || 2)) s = "0" + s;
    return s;
  }
  function formatDate(v, fmt) {
    var d = parseDate(v);
    if (!d) return toStr(v);
    fmt = fmt || "DD/MM/YYYY";
    var H = d.getHours();
    var h = H % 12 === 0 ? 12 : H % 12;
    return fmt.replace(/YYYY|YY|MMMM|MMM|MM|M|DDDD|DDD|DD|D|dddd|ddd|HH|H|hh|h|mm|ss|A|a/g, function (t) {
      switch (t) {
        case "YYYY": return String(d.getFullYear());
        case "YY": return pad(d.getFullYear() % 100);
        case "MMMM": return MONTHS[d.getMonth()];
        case "MMM": return MONTHS[d.getMonth()].slice(0, 3);
        case "MM": return pad(d.getMonth() + 1);
        case "M": return String(d.getMonth() + 1);
        case "DD": return pad(d.getDate());
        case "D": return String(d.getDate());
        case "DDDD":
        case "dddd": return DAYS[d.getDay()];
        case "DDD":
        case "ddd": return DAYS[d.getDay()].slice(0, 3);
        case "HH": return pad(H);
        case "H": return String(H);
        case "hh": return pad(h);
        case "h": return String(h);
        case "mm": return pad(d.getMinutes());
        case "ss": return pad(d.getSeconds());
        case "A": return H < 12 ? "AM" : "PM";
        case "a": return H < 12 ? "am" : "pm";
      }
      return t;
    });
  }

  /* ------------------------------------------------------------------ *
   * Numbers: Indian grouping and amount in words
   * ------------------------------------------------------------------ */
  function groupIndian(intStr) {
    if (intStr.length <= 3) return intStr;
    var last3 = intStr.slice(-3);
    var rest = intStr.slice(0, -3);
    return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
  }
  function groupIntl(intStr) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  function formatNumber(n, decimals, grouping) {
    var neg = n < 0;
    var s = round(Math.abs(n), decimals).toFixed(decimals);
    var parts = s.split(".");
    var ip = grouping === "intl" ? groupIntl(parts[0]) : grouping === "none" ? parts[0] : groupIndian(parts[0]);
    return (neg ? "-" : "") + ip + (parts[1] ? "." + parts[1] : "");
  }
  var ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  var TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  function words99(n) {
    if (n < 20) return ONES[n];
    return TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
  }
  function words999(n) {
    var h = Math.floor(n / 100);
    var r = n % 100;
    return (h ? ONES[h] + " Hundred" + (r ? " " : "") : "") + (r ? words99(r) : "");
  }
  function intWordsIndian(n) {
    n = Math.floor(Math.abs(n));
    if (n === 0) return "Zero";
    var parts = [];
    var crore = Math.floor(n / 10000000);
    n %= 10000000;
    var lakh = Math.floor(n / 100000);
    n %= 100000;
    var thousand = Math.floor(n / 1000);
    n %= 1000;
    if (crore) parts.push((crore > 999 ? intWordsIndian(crore) : words999(crore)) + " Crore");
    if (lakh) parts.push(words99(lakh) + " Lakh");
    if (thousand) parts.push(words99(thousand) + " Thousand");
    if (n) parts.push(words999(n));
    return parts.join(" ");
  }
  // style: "" -> "Rupees Ten Only", "after" -> "Ten Rupees Only", "plain" -> "Ten"
  function amountInWords(v, style) {
    if (style === true) style = "plain";
    var n = round(toNum(v), 2);
    var neg = n < 0;
    n = Math.abs(n);
    var rupees = Math.floor(n);
    var paise = Math.round((n - rupees) * 100);
    if (style === "plain") return (neg ? "Minus " : "") + intWordsIndian(rupees) + (paise ? " Point " + words99(paise) : "");
    if (style === "after") return (neg ? "Minus " : "") + intWordsIndian(rupees) + (rupees === 1 ? " Rupee" : " Rupees") + (paise ? " and " + words99(paise) + " Paise" : "") + " Only";
    var s = (neg ? "Minus " : "") + "Rupees " + intWordsIndian(rupees);
    if (paise) s += " and " + words99(paise) + " Paise";
    return s + " Only";
  }

  /* ------------------------------------------------------------------ *
   * Value formats: "money", "fixed:2", "date:DD/MM/YYYY", "upper", ...
   * Several can be chained with "|".
   * ------------------------------------------------------------------ */
  function applyFormat(v, fmt) {
    if (!fmt) return v;
    var list = isArray(fmt) ? fmt : splitPipes(String(fmt));
    for (var i = 0; i < list.length; i++) v = applyOneFormat(v, list[i]);
    return v;
  }
  function applyOneFormat(v, f) {
    f = String(f || "").trim();
    if (!f || f === "auto") return v;
    var idx = f.indexOf(":");
    var name = (idx >= 0 ? f.slice(0, idx) : f).trim().toLowerCase();
    var arg = idx >= 0 ? f.slice(idx + 1).trim() : "";
    if (arg.length > 1 && (arg.charAt(0) === "'" || arg.charAt(0) === '"')) arg = arg.slice(1, -1);
    switch (name) {
      case "number":
      case "fixed":
        if (isBlank(v)) return "";
        return toNum(v).toFixed(arg === "" ? 2 : parseInt(arg, 10));
      case "money":
      case "amount":
        if (isBlank(v)) return "";
        return formatNumber(toNum(v), arg === "" ? 2 : parseInt(arg, 10), "indian");
      case "intl":
        if (isBlank(v)) return "";
        return formatNumber(toNum(v), arg === "" ? 2 : parseInt(arg, 10), "intl");
      case "currency":
      case "inr":
        if (isBlank(v)) return "";
        return "\u20B9 " + formatNumber(toNum(v), arg === "" ? 2 : parseInt(arg, 10), "indian");
      case "int":
      case "integer":
        if (isBlank(v)) return "";
        return String(Math.round(toNum(v)));
      case "percent":
        if (isBlank(v)) return "";
        return autoNumber(round(toNum(v), arg === "" ? 2 : parseInt(arg, 10))) + "%";
      case "date":
        return isBlank(v) ? "" : formatDate(v, arg || "DD/MM/YYYY");
      case "datetime":
        return isBlank(v) ? "" : formatDate(v, arg || "DD/MM/YYYY hh:mm A");
      case "time":
        return isBlank(v) ? "" : formatDate(v, arg || "hh:mm A");
      case "upper":
        return toStr(v).toUpperCase();
      case "lower":
        return toStr(v).toLowerCase();
      case "title":
      case "proper":
        return toStr(v).toLowerCase().replace(/(^|\s)\S/g, function (c) { return c.toUpperCase(); });
      case "words":
        return isBlank(v) ? "" : amountInWords(v, arg);
      case "blankzero":
      case "hidezero":
        return isNumeric(v) && toNum(v) === 0 ? "" : v;
      case "dash":
        return isBlank(v) || (isNumeric(v) && toNum(v) === 0) ? "-" : v;
      case "default":
        return isBlank(v) ? arg : v;
      case "prefix":
        return isBlank(v) ? v : arg + toStr(v);
      case "suffix":
        return isBlank(v) ? v : toStr(v) + arg;
      case "trim":
        return toStr(v).trim();
      case "maxlen":
      case "truncate":
        var s = toStr(v);
        var n = parseInt(arg, 10) || 20;
        return s.length > n ? s.slice(0, n) : s;
    }
    if (/^[0#,]+(\.0+)?$/.test(f)) {
      if (isBlank(v)) return "";
      var dec = f.indexOf(".") >= 0 ? f.split(".")[1].length : 0;
      return formatNumber(toNum(v), dec, f.indexOf(",") >= 0 ? "indian" : "none");
    }
    return v;
  }
  // Split "a || b | money | date:'DD/MM'" on single pipes outside quotes/brackets.
  function splitPipes(s) {
    var out = [];
    var depth = 0;
    var q = null;
    var cur = "";
    for (var i = 0; i < s.length; i++) {
      var c = s.charAt(i);
      if (q) {
        cur += c;
        if (c === q) q = null;
        continue;
      }
      if (c === "'" || c === '"') {
        q = c;
        cur += c;
        continue;
      }
      if (c === "(" || c === "[") depth++;
      if (c === ")" || c === "]") depth--;
      if (c === "|" && depth === 0) {
        if (s.charAt(i + 1) === "|") {
          cur += "||";
          i++;
          continue;
        }
        out.push(cur);
        cur = "";
        continue;
      }
      cur += c;
    }
    out.push(cur);
    return out;
  }

  /* ------------------------------------------------------------------ *
   * Expression language
   *
   *   order.sot_total_amount - order.sot_total_discount
   *   item_quantity * item_unit_price          (inside a table row)
   *   SUM(col.amount)   PREV(col.balance) + col.amount   ROW(1, item_name)
   *   IF(gst_type = "Intra", tax / 2, 0)   WORDS(var.grand_total)
   * ------------------------------------------------------------------ */
  function ZPError(msg, pos) {
    this.message = msg;
    this.pos = pos;
    this.zp = true;
  }
  ZPError.prototype.toString = function () {
    return this.message;
  };

  var OPS3 = { "===": 1, "!==": 1 };
  var OPS2 = { "==": 1, "!=": 1, "<>": 1, "<=": 1, ">=": 1, "&&": 1, "||": 1 };
  var OPS1 = "+-*/%^&(),.[]?:<>=!";
  var WORD_OPS = { AND: "&&", OR: "||", NOT: "!", MOD: "%" };

  function tokenize(src) {
    var toks = [];
    var i = 0;
    var n = src.length;
    while (i < n) {
      var c = src.charAt(i);
      if (/\s/.test(c)) {
        i++;
        continue;
      }
      var start = i;
      if (/[0-9]/.test(c) || (c === "." && /[0-9]/.test(src.charAt(i + 1)))) {
        while (i < n && /[0-9]/.test(src.charAt(i))) i++;
        if (src.charAt(i) === "." && /[0-9]/.test(src.charAt(i + 1))) {
          i++;
          while (i < n && /[0-9]/.test(src.charAt(i))) i++;
        }
        if (/[eE]/.test(src.charAt(i)) && /[0-9+\-]/.test(src.charAt(i + 1))) {
          i += 2;
          while (i < n && /[0-9]/.test(src.charAt(i))) i++;
        }
        toks.push({ t: "num", v: parseFloat(src.slice(start, i)), p: start });
        continue;
      }
      if (c === '"' || c === "'" || c === "\u201C" || c === "\u2018") {
        var close = c === "\u201C" ? "\u201D" : c === "\u2018" ? "\u2019" : c;
        i++;
        var str = "";
        while (i < n && src.charAt(i) !== close) {
          if (src.charAt(i) === "\\" && i + 1 < n) {
            i++;
            var e = src.charAt(i);
            str += e === "n" ? "\n" : e === "t" ? "\t" : e;
          } else str += src.charAt(i);
          i++;
        }
        if (i >= n) throw new ZPError("Missing closing quote", start);
        i++;
        toks.push({ t: "str", v: str, p: start });
        continue;
      }
      if (/[A-Za-z_\u0080-\uFFFF]/.test(c)) {
        while (i < n && /[A-Za-z0-9_\u0080-\uFFFF]/.test(src.charAt(i))) i++;
        var word = src.slice(start, i);
        var up = word.toUpperCase();
        var k2 = i;
        while (k2 < n && /\s/.test(src.charAt(k2))) k2++;
        // AND(a, b) / OR(...) / NOT(x) / MOD(a, b) are function calls, not operators
        var isCall = src.charAt(k2) === "(" && up !== "NOT";
        if (hasOwn.call(WORD_OPS, up) && !isCall) toks.push({ t: "op", v: WORD_OPS[up], p: start });
        else toks.push({ t: "id", v: word, p: start });
        continue;
      }
      if (c === "{" ) {
        // allow {field} as a friendly alias for a field reference
        var j = src.indexOf("}", i);
        if (j > i) {
          var inner = src.slice(i + 1, j).trim();
          if (/^[A-Za-z_][A-Za-z0-9_.]*$/.test(inner)) {
            var segs = inner.split(".");
            for (var s = 0; s < segs.length; s++) {
              if (s > 0) toks.push({ t: "op", v: ".", p: i });
              toks.push({ t: "id", v: segs[s], p: i });
            }
            i = j + 1;
            continue;
          }
        }
      }
      var three = src.substr(i, 3);
      var two = src.substr(i, 2);
      if (hasOwn.call(OPS3, three)) {
        toks.push({ t: "op", v: three.slice(0, 2), p: i });
        i += 3;
        continue;
      }
      if (hasOwn.call(OPS2, two)) {
        toks.push({ t: "op", v: two, p: i });
        i += 2;
        continue;
      }
      if (c === "\u00D7") c = "*";
      if (c === "\u00F7") c = "/";
      if (OPS1.indexOf(c) >= 0) {
        toks.push({ t: "op", v: c, p: i });
        i++;
        continue;
      }
      throw new ZPError("Unexpected character '" + c + "'", i);
    }
    toks.push({ t: "eof", p: n });
    return toks;
  }

  var PREC = {
    "?": 1,
    "||": 2,
    "&&": 3,
    "==": 4, "!=": 4, "<>": 4, "=": 4, "<": 4, ">": 4, "<=": 4, ">=": 4,
    "&": 5,
    "+": 6, "-": 6,
    "*": 7, "/": 7, "%": 7,
    "^": 9
  };

  function Parser(src) {
    this.src = src;
    this.toks = tokenize(src);
    this.i = 0;
  }
  Parser.prototype.peek = function () {
    return this.toks[this.i];
  };
  Parser.prototype.next = function () {
    return this.toks[this.i++];
  };
  Parser.prototype.isOp = function (v) {
    var t = this.peek();
    return t.t === "op" && t.v === v;
  };
  Parser.prototype.expect = function (v) {
    var t = this.next();
    if (t.t !== "op" || t.v !== v) throw new ZPError("Expected '" + v + "'", t.p);
    return t;
  };
  Parser.prototype.parseAll = function () {
    if (this.peek().t === "eof") return { t: "null" };
    var e = this.parseExpr(0);
    var t = this.peek();
    if (t.t !== "eof") throw new ZPError("Unexpected '" + (t.v === undefined ? "" : t.v) + "'", t.p);
    return e;
  };
  Parser.prototype.parseExpr = function (minPrec) {
    var left = this.parseUnary();
    for (;;) {
      var t = this.peek();
      if (t.t !== "op" || !hasOwn.call(PREC, t.v)) break;
      var p = PREC[t.v];
      if (p < minPrec) break;
      this.next();
      if (t.v === "?") {
        var a = this.parseExpr(0);
        this.expect(":");
        var b = this.parseExpr(1);
        left = { t: "tern", c: left, a: a, b: b };
        continue;
      }
      var right = this.parseExpr(t.v === "^" ? p : p + 1);
      var op = t.v === "=" ? "==" : t.v === "<>" ? "!=" : t.v;
      left = { t: "bin", op: op, l: left, r: right };
    }
    return left;
  };
  Parser.prototype.parseUnary = function () {
    var t = this.peek();
    if (t.t === "op" && (t.v === "-" || t.v === "+" || t.v === "!")) {
      this.next();
      // unary binds tighter than * but looser than ^ (so -2^2 = -4)
      return { t: "un", op: t.v, a: this.parseExpr(8) };
    }
    return this.parsePostfix(this.parsePrimary());
  };
  Parser.prototype.parsePrimary = function () {
    var t = this.next();
    if (t.t === "num") return { t: "num", v: t.v };
    if (t.t === "str") return { t: "str", v: t.v };
    if (t.t === "id") {
      var up = t.v.toUpperCase();
      if (this.isOp("(")) {
        this.next();
        var args = [];
        if (!this.isOp(")")) {
          for (;;) {
            args.push(this.parseExpr(0));
            if (this.isOp(",")) {
              this.next();
              continue;
            }
            break;
          }
        }
        this.expect(")");
        return { t: "call", n: up, a: args, p: t.p };
      }
      if (up === "TRUE") return { t: "bool", v: true };
      if (up === "FALSE") return { t: "bool", v: false };
      if (up === "NULL" || up === "BLANK") return { t: "null" };
      return { t: "id", n: t.v, p: t.p };
    }
    if (t.t === "op" && t.v === "(") {
      var e = this.parseExpr(0);
      this.expect(")");
      return e;
    }
    if (t.t === "eof") throw new ZPError("Formula ends unexpectedly", t.p);
    throw new ZPError("Unexpected '" + t.v + "'", t.p);
  };
  Parser.prototype.parsePostfix = function (node) {
    for (;;) {
      if (this.isOp(".")) {
        this.next();
        var t = this.next();
        if (t.t !== "id" && t.t !== "num") throw new ZPError("Expected a field name after '.'", t.p);
        node = { t: "mem", o: node, p: String(t.v) };
        continue;
      }
      if (this.isOp("[")) {
        this.next();
        var e = this.parseExpr(0);
        this.expect("]");
        node = { t: "idx", o: node, e: e };
        continue;
      }
      return node;
    }
  };

  var astCache = {};
  var astCacheSize = 0;
  function parse(src) {
    src = src == null ? "" : String(src);
    if (hasOwn.call(astCache, src)) return astCache[src];
    var ast = new Parser(src).parseAll();
    if (astCacheSize > 2000) {
      astCache = {};
      astCacheSize = 0;
    }
    astCache[src] = ast;
    astCacheSize++;
    return ast;
  }

  /* ------------------------------------------------------------------ *
   * Data context
   * ------------------------------------------------------------------ */
  var COLLECTIONS = { items: 1, payments: 1, tax: 1, batches: 1, creditNotes: 1 };
  var ROOTS = { order: 1, store: 1, extra: 1, data: 1, item: 1, row: 1, col: 1, "var": 1, table: 1 };

  function normalizeData(raw) {
    raw = raw || {};
    var items = isArray(raw.items) ? raw.items : [];
    var batches = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      var bl = it.itemBatchData || it.batchData;
      if (isArray(bl) && bl.length) {
        for (var j = 0; j < bl.length; j++) batches.push(extend(it, bl[j]));
      } else batches.push(it);
    }
    var extra = raw.extra || {};
    return {
      order: raw.order || {},
      store: raw.store || {},
      payments: isArray(raw.payments) ? raw.payments : [],
      items: items,
      tax: isArray(raw.tax) ? raw.tax : [],
      batches: batches,
      creditNotes: isArray(raw.creditNotes) ? raw.creditNotes : isArray(extra.creditNotes) ? extra.creditNotes : [],
      extra: extra
    };
  }

  function Context(template, rawData, opts) {
    this.template = template || {};
    this.data = normalizeData(rawData);
    this.opts = opts || {};
    this.vars = {};
    this.varState = {};
    this.tables = {};
    this.tableList = [];
    this.warnings = {};
    this.now = this.opts.now ? new Date(this.opts.now) : new Date();
    var vs = this.template.variables || [];
    this.varDefs = {};
    for (var i = 0; i < vs.length; i++) if (vs[i] && vs[i].name) this.varDefs[vs[i].name] = vs[i];
    this.prepareTables();
  }
  Context.prototype.warn = function (msg) {
    this.warnings[msg] = true;
  };
  Context.prototype.rootScope = function () {
    return { ctx: this, row: null, rowIndex: -1, table: null, bind: null };
  };
  Context.prototype.prepareTables = function () {
    var bands = this.template.bands || [];
    for (var b = 0; b < bands.length; b++) {
      var band = bands[b];
      var els = band.elements || [];
      for (var e = 0; e < els.length; e++) {
        if (els[e] && els[e].type === "table") this.registerTable(els[e]);
      }
    }
  };
  Context.prototype.registerTable = function (el) {
    var info = { el: el, name: el.name || el.id, rowsCache: null, colByKey: {}, cache: [], state: [] };
    var cols = el.columns || [];
    for (var i = 0; i < cols.length; i++) if (cols[i] && cols[i].key) info.colByKey[cols[i].key] = cols[i];
    this.tables[el.id] = info;
    if (el.name && !this.tables[el.name]) this.tables[el.name] = info;
    this.tableList.push(info);
    return info;
  };
  Context.prototype.collection = function (name) {
    var d = this.data;
    if (name === "items") return d.items;
    if (name === "batches") return d.batches;
    if (name === "payments") return d.payments;
    if (name === "tax") return d.tax;
    if (name === "creditNotes") return d.creditNotes;
    return null;
  };
  Context.prototype.tableRows = function (info) {
    if (info.rowsCache) return info.rowsCache;
    var el = info.el;
    var src = el.source || "items";
    var rows;
    info.rowsCache = [];
    if (src === "expr") {
      var v = this.evalSafe(el.sourceExpr, this.rootScope()).value;
      rows = isArray(v) ? v : [];
    } else rows = this.collection(src) || [];
    var out = [];
    var i;
    if (el.filter && String(el.filter).trim()) {
      for (i = 0; i < rows.length; i++) {
        var sc = { ctx: this, row: rows[i], rowIndex: i, table: null, bind: src };
        if (truthy(this.evalSafe(el.filter, sc).value)) out.push(rows[i]);
      }
    } else out = rows.slice();
    if (el.sortBy && String(el.sortBy).trim()) {
      var self = this;
      var keyed = [];
      for (i = 0; i < out.length; i++) {
        keyed.push({ r: out[i], k: self.evalSafe(el.sortBy, { ctx: self, row: out[i], rowIndex: i, table: null, bind: src }).value, i: i });
      }
      var dir = el.sortDir === "desc" ? -1 : 1;
      keyed.sort(function (a, b) {
        var x = a.k;
        var y = b.k;
        var c;
        if (isNumeric(x) && isNumeric(y)) c = toNum(x) - toNum(y);
        else c = toStr(x).localeCompare(toStr(y));
        return c === 0 ? a.i - b.i : c * dir;
      });
      out = [];
      for (i = 0; i < keyed.length; i++) out.push(keyed[i].r);
    }
    info.rowsCache = out;
    return out;
  };
  Context.prototype.defaultTable = function () {
    for (var i = 0; i < this.tableList.length; i++) {
      var s = this.tableList[i].el.source || "items";
      if (s === "items" || s === "batches") return this.tableList[i];
    }
    return this.tableList[0] || null;
  };
  Context.prototype.findTable = function (name) {
    if (!name) return null;
    return this.tables[name] || null;
  };
  Context.prototype.rowScope = function (info, idx) {
    var rows = this.tableRows(info);
    return { ctx: this, row: rows[idx] || null, rowIndex: idx, table: info, bind: info.el.source || "items" };
  };
  Context.prototype.colValue = function (info, idx, key) {
    var col = info.colByKey[key];
    if (!col) {
      this.warn("Unknown column 'col." + key + "'");
      return null;
    }
    var cache = info.cache[idx] || (info.cache[idx] = {});
    var state = info.state[idx] || (info.state[idx] = {});
    if (hasOwn.call(cache, key)) return cache[key];
    if (state[key] === 1) throw new ZPError("Circular reference in column '" + key + "'");
    state[key] = 1;
    try {
      var v = this.evaluate(parse(col.expr), this.rowScope(info, idx));
      cache[key] = v;
      return v;
    } finally {
      state[key] = 2;
    }
  };
  Context.prototype.varValue = function (name) {
    if (hasOwn.call(this.vars, name)) return this.vars[name];
    var def = this.varDefs[name];
    if (!def) {
      this.warn("Unknown variable 'var." + name + "'");
      return null;
    }
    if (this.varState[name] === 1) throw new ZPError("Circular reference in variable '" + name + "'");
    this.varState[name] = 1;
    try {
      var v = this.evaluate(parse(def.expr), this.rootScope());
      this.vars[name] = v;
      return v;
    } finally {
      this.varState[name] = 2;
    }
  };

  function Getter(fn, label) {
    this.get = fn;
    this.label = label;
  }

  Context.prototype.lookup = function (name, scope) {
    if (scope.bindName && name === scope.bindName) return scope.row;
    if (scope.bind && name === scope.bind && scope.row) return scope.row;
    var self = this;
    var d = this.data;
    switch (name) {
      case "order": return d.order;
      case "store": return d.store;
      case "extra": return d.extra;
      case "data": return d;
      case "item":
      case "row":
        return scope.row;
      case "col":
        var tinfo = scope.table || this.defaultTable();
        return new Getter(function (k) {
          if (!tinfo) {
            self.warn("No table on this template for 'col." + k + "'");
            return null;
          }
          if (scope.table && scope.rowIndex >= 0) return self.colValue(tinfo, scope.rowIndex, k);
          // outside a row: col.x means the whole column (useful in SUM/JOIN)
          var rows = self.tableRows(tinfo);
          var out = [];
          for (var i = 0; i < rows.length; i++) out.push(self.colValue(tinfo, i, k));
          return out;
        }, "col");
      case "var":
        return new Getter(function (k) {
          return self.varValue(k);
        }, "var");
      case "table":
        return new Getter(function (k) {
          var t = self.findTable(k);
          return t ? self.tableRows(t) : null;
        }, "table");
    }
    if (hasOwn.call(COLLECTIONS, name)) return this.collection(name);
    if (scope.row && typeof scope.row === "object" && hasOwn.call(scope.row, name)) return scope.row[name];
    if (scope.table && scope.rowIndex >= 0 && hasOwn.call(scope.table.colByKey, name)) return this.colValue(scope.table, scope.rowIndex, name);
    if (hasOwn.call(this.varDefs, name)) return this.varValue(name);
    if (hasOwn.call(d.order, name)) return d.order[name];
    if (hasOwn.call(d.store, name)) return d.store[name];
    if (d.payments[0] && hasOwn.call(d.payments[0], name)) return d.payments[0][name];
    if (hasOwn.call(d.extra, name)) return d.extra[name];
    var lower = name.toLowerCase();
    if (lower === "sno" || lower === "rownum" || lower === "s_no") return scope.rowIndex >= 0 ? scope.rowIndex + 1 : null;
    this.warn("Unknown field '" + name + "'");
    return null;
  };

  function member(obj, key, ctx) {
    if (obj === null || obj === undefined) return null;
    if (obj instanceof Getter) return obj.get(key);
    if (isArray(obj)) {
      if (/^\d+$/.test(key)) return obj[parseInt(key, 10)];
      if (key === "length" || key === "count") return obj.length;
      var out = [];
      for (var i = 0; i < obj.length; i++) out.push(obj[i] == null ? null : obj[i][key]);
      return out;
    }
    if (typeof obj === "string") {
      // nested JSON stored as text (e.g. ship-to address columns)
      if (/^\s*[\[{]/.test(obj)) {
        try {
          return member(JSON.parse(obj), key, ctx);
        } catch (e) {}
      }
      if (key === "length") return obj.length;
      return null;
    }
    if (typeof obj === "object") return hasOwn.call(obj, key) ? obj[key] : null;
    return null;
  }

  // Which rows does an aggregate like SUM(...) walk over?
  function collectRoots(ast, acc) {
    if (!ast || typeof ast !== "object") return acc;
    if (ast.t === "mem" || ast.t === "idx") {
      var base = ast.o;
      while (base && (base.t === "mem" || base.t === "idx")) base = base.o;
      if (base && base.t === "id") acc[base.n] = true;
      collectRoots(ast.o, acc);
      if (ast.e) collectRoots(ast.e, acc);
      return acc;
    }
    if (ast.t === "id") {
      acc["#bare"] = true;
      return acc;
    }
    if (ast.t === "call") {
      // nested aggregates are self contained
      if (AGGREGATES[ast.n]) return acc;
      for (var i = 0; i < ast.a.length; i++) collectRoots(ast.a[i], acc);
      return acc;
    }
    if (ast.l) collectRoots(ast.l, acc);
    if (ast.r) collectRoots(ast.r, acc);
    if (ast.a && !isArray(ast.a)) collectRoots(ast.a, acc);
    if (ast.c) collectRoots(ast.c, acc);
    if (ast.b) collectRoots(ast.b, acc);
    return acc;
  }

  // Returns an array of scopes to evaluate an aggregate argument in.
  Context.prototype.aggregateScopes = function (exprAst, sourceName, scope) {
    var i;
    var out = [];
    var info = null;
    var coll = null;
    var collName = null;
    if (sourceName) {
      if (hasOwn.call(COLLECTIONS, sourceName)) {
        coll = this.collection(sourceName);
        collName = sourceName;
      } else {
        info = this.findTable(sourceName);
        if (!info) throw new ZPError("Unknown table or list '" + sourceName + "'");
      }
    } else {
      var roots = collectRoots(exprAst, {});
      var named = [];
      for (var k in roots) if (hasOwn.call(COLLECTIONS, k)) named.push(k);
      if (named.length === 1 && !roots.col) {
        coll = this.collection(named[0]);
        collName = named[0];
      } else if (named.length > 1) {
        throw new ZPError("A SUM/COUNT can only walk one list at a time (" + named.join(", ") + ")");
      } else {
        info = scope.table || this.defaultTable();
        if (!info) {
          coll = this.data.items;
          collName = "items";
        }
      }
    }
    if (info) {
      var rows = this.tableRows(info);
      for (i = 0; i < rows.length; i++) out.push(this.rowScope(info, i));
    } else if (coll) {
      for (i = 0; i < coll.length; i++) out.push({ ctx: this, row: coll[i], rowIndex: i, table: null, bind: collName, bindName: collName });
    }
    return out;
  };

  var AGGREGATES = { SUM: 1, AVG: 1, AVERAGE: 1, COUNT: 1, FIRST: 1, LAST: 1, JOIN: 1, SUMIF: 1, COUNTIF: 1, AVGIF: 1, MINIF: 1, MAXIF: 1, RUNSUM: 1, PREV: 1, NEXT: 1, ROW: 1, MIN: 1, MAX: 1, DISTINCT: 1, ROWCOUNT: 1 };

  function argStr(ast) {
    if (!ast) return null;
    if (ast.t === "str") return ast.v;
    if (ast.t === "id") return ast.n;
    throw new ZPError("Expected a list or table name");
  }

  Context.prototype.callAggregate = function (node, scope) {
    var self = this;
    var a = node.a;
    var n = node.n;
    var i;
    var scopes;
    var vals;
    function each(exprAst, srcAst, condAst) {
      var ss = self.aggregateScopes(condAst ? { t: "bin", op: "+", l: condAst, r: exprAst || { t: "null" } } : exprAst, srcAst ? argStr(srcAst) : null, scope);
      var res = [];
      for (var j = 0; j < ss.length; j++) {
        if (condAst && !truthy(self.evaluate(condAst, ss[j]))) continue;
        res.push(exprAst ? self.evaluate(exprAst, ss[j]) : 1);
      }
      return res;
    }
    function flat(list) {
      var out = [];
      for (var j = 0; j < list.length; j++) {
        if (isArray(list[j])) for (var k = 0; k < list[j].length; k++) out.push(list[j][k]);
        else out.push(list[j]);
      }
      return out;
    }
    switch (n) {
      case "MIN":
      case "MAX":
        if (a.length !== 1) {
          vals = [];
          for (i = 0; i < a.length; i++) vals.push(toNum(this.evaluate(a[i], scope)));
          return vals.length ? Math[n === "MIN" ? "min" : "max"].apply(null, vals) : 0;
        }
        vals = flat(each(a[0], null));
        var nums = [];
        for (i = 0; i < vals.length; i++) if (!isBlank(vals[i])) nums.push(toNum(vals[i]));
        return nums.length ? Math[n === "MIN" ? "min" : "max"].apply(null, nums) : 0;
      case "SUM":
        vals = flat(each(a[0], a[1]));
        var s = 0;
        for (i = 0; i < vals.length; i++) s += toNum(vals[i]);
        return round(s, 10);
      case "AVG":
      case "AVERAGE":
        vals = flat(each(a[0], a[1]));
        if (!vals.length) return 0;
        var t = 0;
        for (i = 0; i < vals.length; i++) t += toNum(vals[i]);
        return t / vals.length;
      case "COUNT":
        if (!a.length) return each(null, null).length;
        if (a.length === 1 && a[0].t === "str") return each(null, a[0]).length;
        vals = flat(each(a[0], a[1]));
        var c = 0;
        for (i = 0; i < vals.length; i++) if (!isBlank(vals[i])) c++;
        return c;
      case "ROWCOUNT":
        return each(null, a[0] || null).length;
      case "FIRST":
        vals = flat(each(a[0], a[1]));
        return vals.length ? vals[0] : null;
      case "LAST":
        vals = flat(each(a[0], a[1]));
        return vals.length ? vals[vals.length - 1] : null;
      case "JOIN":
      case "DISTINCT":
        vals = flat(each(a[0], a[2] || null));
        var sep = a[1] ? toStr(this.evaluate(a[1], scope)) : ", ";
        var seen = {};
        var parts = [];
        for (i = 0; i < vals.length; i++) {
          if (isBlank(vals[i])) continue;
          var sv = toStr(vals[i]);
          if (n === "DISTINCT" && seen[sv]) continue;
          seen[sv] = 1;
          parts.push(sv);
        }
        return parts.join(sep);
      case "SUMIF":
        vals = each(a[1], a[2], a[0]);
        var ss = 0;
        for (i = 0; i < vals.length; i++) ss += toNum(vals[i]);
        return round(ss, 10);
      case "AVGIF":
        vals = each(a[1], a[2], a[0]);
        if (!vals.length) return 0;
        var at = 0;
        for (i = 0; i < vals.length; i++) at += toNum(vals[i]);
        return at / vals.length;
      case "MINIF":
      case "MAXIF":
        vals = each(a[1], a[2], a[0]);
        var nn = [];
        for (i = 0; i < vals.length; i++) nn.push(toNum(vals[i]));
        return nn.length ? Math[n === "MINIF" ? "min" : "max"].apply(null, nn) : 0;
      case "COUNTIF":
        return each(null, a[1], a[0]).length;
      case "RUNSUM":
      case "PREV":
      case "NEXT":
      case "ROW":
        return this.rowFunction(node, scope);
    }
    return null;
  };

  // Functions that look at other rows of the same table.
  Context.prototype.rowFunction = function (node, scope) {
    var a = node.a;
    var n = node.n;
    var info = scope.table;
    var i;
    if (n === "ROW") {
      if (a.length < 2) throw new ZPError("ROW needs a row number and a value: ROW(1, col.amount)");
      var srcName = a[2] ? argStr(a[2]) : null;
      var target = srcName ? this.findTable(srcName) : info || this.defaultTable();
      var rn = Math.floor(toNum(this.evaluate(a[0], scope)));
      if (!target) {
        var coll = srcName && hasOwn.call(COLLECTIONS, srcName) ? this.collection(srcName) : this.data.items;
        var cname = srcName && hasOwn.call(COLLECTIONS, srcName) ? srcName : "items";
        if (rn < 0) rn = coll.length + rn + 1;
        if (rn < 1 || rn > coll.length) return null;
        return this.evaluate(a[1], { ctx: this, row: coll[rn - 1], rowIndex: rn - 1, table: null, bind: cname, bindName: cname });
      }
      var rowsT = this.tableRows(target);
      if (rn < 0) rn = rowsT.length + rn + 1; // ROW(-1, ...) = last row
      if (rn < 1 || rn > rowsT.length) return null;
      return this.evaluate(a[1], this.rowScope(target, rn - 1));
    }
    if (!info || scope.rowIndex < 0) {
      if (n === "RUNSUM") return this.callAggregate({ t: "call", n: "SUM", a: a }, scope);
      throw new ZPError(n + "() only works inside a table column");
    }
    var rows = this.tableRows(info);
    var idx = scope.rowIndex;
    if (n === "PREV" || n === "NEXT") {
      var steps = a[2] ? Math.max(1, Math.floor(toNum(this.evaluate(a[2], scope)))) : 1;
      var j = n === "PREV" ? idx - steps : idx + steps;
      if (j < 0 || j >= rows.length) return a[1] ? this.evaluate(a[1], scope) : null;
      return this.evaluate(a[0], this.rowScope(info, j));
    }
    // RUNSUM: running total down to (and including) this row
    var total = 0;
    for (i = 0; i <= idx; i++) total += toNum(this.evaluate(a[0], this.rowScope(info, i)));
    return round(total, 10);
  };

  function strOf(v) {
    return toStr(v);
  }

  Context.prototype.callFunction = function (node, scope) {
    var n = node.n;
    var a = node.a;
    var self = this;
    if (hasOwn.call(AGGREGATES, n)) return this.callAggregate(node, scope);
    function ev(i) {
      return i < a.length ? self.evaluate(a[i], scope) : null;
    }
    var i;
    var v;
    switch (n) {
      case "IF":
        return truthy(ev(0)) ? ev(1) : a.length > 2 ? ev(2) : "";
      case "IFS":
        for (i = 0; i + 1 < a.length; i += 2) if (truthy(ev(i))) return ev(i + 1);
        return a.length % 2 ? ev(a.length - 1) : "";
      case "SWITCH":
        v = ev(0);
        for (i = 1; i + 1 < a.length; i += 2) if (toStr(ev(i)) === toStr(v)) return ev(i + 1);
        return a.length % 2 === 0 ? ev(a.length - 1) : "";
      case "AND":
        for (i = 0; i < a.length; i++) if (!truthy(ev(i))) return false;
        return true;
      case "OR":
        for (i = 0; i < a.length; i++) if (truthy(ev(i))) return true;
        return false;
      case "NOT":
        return !truthy(ev(0));
      case "COALESCE":
      case "IFBLANK":
      case "IFEMPTY":
        for (i = 0; i < a.length; i++) {
          v = ev(i);
          if (!isBlank(v) && !(isArray(v) && !v.length)) return v;
        }
        return null;
      case "ISBLANK":
        v = ev(0);
        return isBlank(v) || (typeof v === "string" && v.trim() === "");
      case "ISNUMBER":
        return isNumeric(ev(0));
      case "ROUND":
        return round(toNum(ev(0)), a.length > 1 ? Math.floor(toNum(ev(1))) : 0);
      case "ROUNDUP":
        var fu = Math.pow(10, a.length > 1 ? Math.floor(toNum(ev(1))) : 0);
        v = toNum(ev(0));
        return (v < 0 ? -Math.ceil(-v * fu - 1e-9) : Math.ceil(v * fu - 1e-9)) / fu;
      case "ROUNDDOWN":
        var fd = Math.pow(10, a.length > 1 ? Math.floor(toNum(ev(1))) : 0);
        v = toNum(ev(0));
        return (v < 0 ? -Math.floor(-v * fd + 1e-9) : Math.floor(v * fd + 1e-9)) / fd;
      case "FLOOR":
        return Math.floor(toNum(ev(0)));
      case "CEIL":
      case "CEILING":
        return Math.ceil(toNum(ev(0)));
      case "INT":
      case "TRUNC":
        v = toNum(ev(0));
        return v < 0 ? Math.ceil(v) : Math.floor(v);
      case "ABS":
        return Math.abs(toNum(ev(0)));
      case "MOD":
        var dv = toNum(ev(1));
        return dv === 0 ? 0 : toNum(ev(0)) % dv;
      case "POWER":
      case "POW":
        return Math.pow(toNum(ev(0)), toNum(ev(1)));
      case "SQRT":
        return Math.sqrt(Math.max(0, toNum(ev(0))));
      case "PERCENT":
        // PERCENT(part, whole) -> part as % of whole
        var whole = toNum(ev(1));
        return whole === 0 ? 0 : (toNum(ev(0)) * 100) / whole;
      case "NUM":
      case "NUMBER":
      case "VALUE":
        return toNum(ev(0));
      case "TEXT":
      case "FORMAT":
        return toStr(applyFormat(ev(0), toStr(ev(1))));
      case "WORDS":
        return amountInWords(ev(0), a.length > 1 ? toStr(ev(1)).toLowerCase() : "");
      case "CONCAT":
        var sb = "";
        for (i = 0; i < a.length; i++) sb += strOf(ev(i));
        return sb;
      case "UPPER":
        return strOf(ev(0)).toUpperCase();
      case "LOWER":
        return strOf(ev(0)).toLowerCase();
      case "PROPER":
        return strOf(ev(0)).toLowerCase().replace(/(^|\s)\S/g, function (c) { return c.toUpperCase(); });
      case "TRIM":
        return strOf(ev(0)).trim();
      case "LEN":
        return strOf(ev(0)).length;
      case "LEFT":
        return strOf(ev(0)).slice(0, a.length > 1 ? Math.max(0, toNum(ev(1))) : 1);
      case "RIGHT":
        var rs = strOf(ev(0));
        var rn = a.length > 1 ? Math.max(0, toNum(ev(1))) : 1;
        return rn ? rs.slice(-rn) : "";
      case "MID":
      case "SUBSTR":
        return strOf(ev(0)).substr(Math.max(0, toNum(ev(1)) - 1), a.length > 2 ? toNum(ev(2)) : undefined);
      case "REPLACE":
      case "SUBSTITUTE":
        return strOf(ev(0)).split(strOf(ev(1))).join(strOf(ev(2)));
      case "CONTAINS":
        return strOf(ev(0)).toLowerCase().indexOf(strOf(ev(1)).toLowerCase()) >= 0;
      case "STARTSWITH":
        return strOf(ev(0)).toLowerCase().indexOf(strOf(ev(1)).toLowerCase()) === 0;
      case "REPEAT":
        var rp = "";
        var times = Math.min(500, Math.max(0, Math.floor(toNum(ev(1)))));
        for (i = 0; i < times; i++) rp += strOf(ev(0));
        return rp;
      case "DATE":
        return formatDate(ev(0), a.length > 1 ? strOf(ev(1)) : "DD/MM/YYYY");
      case "TODAY":
        return formatDate(this.now, a.length ? strOf(ev(0)) : "DD/MM/YYYY");
      case "NOW":
        return formatDate(this.now, a.length ? strOf(ev(0)) : "DD/MM/YYYY hh:mm A");
      case "ADDDAYS":
        var bd = parseDate(ev(0));
        if (!bd) return null;
        var nd = new Date(bd.getTime());
        nd.setDate(nd.getDate() + Math.floor(toNum(ev(1))));
        return formatDate(nd, a.length > 2 ? strOf(ev(2)) : "DD/MM/YYYY");
      case "DAYS":
        var d1 = parseDate(ev(0));
        var d2 = parseDate(ev(1));
        if (!d1 || !d2) return null;
        return Math.round((d1.getTime() - d2.getTime()) / 86400000);
      case "SNO":
      case "ROWNUM":
        return scope.rowIndex >= 0 ? scope.rowIndex + 1 : null;
      case "PAGEWIDTH":
        return paperSize(this.template).width;
    }
    throw new ZPError("Unknown function " + n + "()", node.p);
  };

  function cmp(l, r) {
    if ((isNumeric(l) || isBlank(l)) && (isNumeric(r) || isBlank(r)) && !(isBlank(l) && isBlank(r))) {
      if (isBlank(l) || isBlank(r)) {
        // blank vs value: compare as text so "" != 0
        var ls = toStr(l);
        var rs = toStr(r);
        return ls < rs ? -1 : ls > rs ? 1 : 0;
      }
      var a = toNum(l);
      var b = toNum(r);
      return a < b ? -1 : a > b ? 1 : 0;
    }
    var x = toStr(l).toLowerCase();
    var y = toStr(r).toLowerCase();
    return x < y ? -1 : x > y ? 1 : 0;
  }

  Context.prototype.evaluate = function (node, scope) {
    switch (node.t) {
      case "num":
      case "str":
      case "bool":
        return node.v;
      case "null":
        return null;
      case "id":
        return this.lookup(node.n, scope);
      case "mem":
        return member(this.evaluate(node.o, scope), node.p, this);
      case "idx":
        var base = this.evaluate(node.o, scope);
        var key = this.evaluate(node.e, scope);
        if (isArray(base) && isNumeric(key)) return base[Math.floor(toNum(key))];
        return member(base, toStr(key), this);
      case "call":
        return this.callFunction(node, scope);
      case "un":
        var v = this.evaluate(node.a, scope);
        if (node.op === "-") return -toNum(v);
        if (node.op === "+") return toNum(v);
        return !truthy(v);
      case "tern":
        return truthy(this.evaluate(node.c, scope)) ? this.evaluate(node.a, scope) : this.evaluate(node.b, scope);
      case "bin":
        var op = node.op;
        if (op === "&&") return truthy(this.evaluate(node.l, scope)) ? truthy(this.evaluate(node.r, scope)) : false;
        if (op === "||") {
          var lv = this.evaluate(node.l, scope);
          return truthy(lv) ? true : truthy(this.evaluate(node.r, scope));
        }
        var l = this.evaluate(node.l, scope);
        var r = this.evaluate(node.r, scope);
        switch (op) {
          case "+":
            if ((isNumeric(l) || isBlank(l)) && (isNumeric(r) || isBlank(r))) return round(toNum(l) + toNum(r), 10);
            return toStr(l) + toStr(r);
          case "&":
            return toStr(l) + toStr(r);
          case "-":
            return round(toNum(l) - toNum(r), 10);
          case "*":
            return round(toNum(l) * toNum(r), 10);
          case "/":
            var dn = toNum(r);
            return dn === 0 ? 0 : toNum(l) / dn;
          case "%":
            var md = toNum(r);
            return md === 0 ? 0 : toNum(l) % md;
          case "^":
            return Math.pow(toNum(l), toNum(r));
          case "==":
            return cmp(l, r) === 0;
          case "!=":
            return cmp(l, r) !== 0;
          case "<":
            return cmp(l, r) < 0;
          case ">":
            return cmp(l, r) > 0;
          case "<=":
            return cmp(l, r) <= 0;
          case ">=":
            return cmp(l, r) >= 0;
        }
    }
    throw new ZPError("Cannot evaluate formula");
  };

  Context.prototype.evalSafe = function (src, scope) {
    try {
      if (src === null || src === undefined || String(src).trim() === "") return { value: null };
      return { value: this.evaluate(parse(src), scope || this.rootScope()) };
    } catch (e) {
      return { value: null, error: e && e.zp ? e.message : String((e && e.message) || e) };
    }
  };

  // "Invoice {{ inv_no }} dated {{ created_date | date:'DD MMM YYYY' }}"
  function parseTemplateText(s) {
    var out = [];
    var i = 0;
    s = s == null ? "" : String(s);
    while (i < s.length) {
      var a = s.indexOf("{{", i);
      if (a < 0) {
        out.push({ text: s.slice(i) });
        break;
      }
      if (a > i) out.push({ text: s.slice(i, a) });
      var b = s.indexOf("}}", a + 2);
      if (b < 0) {
        out.push({ text: s.slice(a) });
        break;
      }
      var inner = s.slice(a + 2, b);
      var parts = splitPipes(inner);
      out.push({ expr: parts[0].trim().replace(/^=/, ""), fmt: parts.slice(1) });
      i = b + 2;
    }
    return out;
  }

  Context.prototype.interpolate = function (s, scope, errors) {
    var parts = parseTemplateText(s);
    var out = "";
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (p.text !== undefined) {
        out += p.text;
        continue;
      }
      var r = this.evalSafe(p.expr, scope);
      if (r.error) {
        if (errors) errors.push(r.error);
        out += "#ERR";
        continue;
      }
      out += toStr(applyFormat(r.value, p.fmt));
    }
    return out;
  };

  /* ------------------------------------------------------------------ *
   * QR code (byte mode, versions 1-40). Based on the public-domain
   * algorithm description by Project Nayuki.
   * ------------------------------------------------------------------ */
  var QR_ECC = {
    L: { f: 1, cw: [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], nb: [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25] },
    M: { f: 0, cw: [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28], nb: [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49] },
    Q: { f: 3, cw: [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], nb: [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68] },
    H: { f: 2, cw: [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], nb: [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81] }
  };
  function qrRawModules(ver) {
    var r = (16 * ver + 128) * ver + 64;
    if (ver >= 2) {
      var na = Math.floor(ver / 7) + 2;
      r -= (25 * na - 10) * na - 55;
      if (ver >= 7) r -= 36;
    }
    return r;
  }
  function qrDataCodewords(ver, e) {
    return Math.floor(qrRawModules(ver) / 8) - QR_ECC[e].cw[ver] * QR_ECC[e].nb[ver];
  }
  function gfMul(x, y) {
    var z = 0;
    for (var i = 7; i >= 0; i--) {
      z = (z << 1) ^ ((z >>> 7) * 0x11d);
      z ^= ((y >>> i) & 1) * x;
    }
    return z & 0xff;
  }
  function rsDivisor(deg) {
    var res = [];
    var i;
    for (i = 0; i < deg - 1; i++) res.push(0);
    res.push(1);
    var root = 1;
    for (i = 0; i < deg; i++) {
      for (var j = 0; j < res.length; j++) {
        res[j] = gfMul(res[j], root);
        if (j + 1 < res.length) res[j] ^= res[j + 1];
      }
      root = gfMul(root, 0x02);
    }
    return res;
  }
  function rsRemainder(data, div) {
    var res = [];
    var i;
    for (i = 0; i < div.length; i++) res.push(0);
    for (i = 0; i < data.length; i++) {
      var factor = data[i] ^ res.shift();
      res.push(0);
      for (var j = 0; j < div.length; j++) res[j] ^= gfMul(div[j], factor);
    }
    return res;
  }
  function utf8Bytes(s) {
    var b = [];
    var enc = unescape(encodeURIComponent(s));
    for (var i = 0; i < enc.length; i++) b.push(enc.charCodeAt(i));
    return b;
  }
  function qrEncode(text, ecl, forceMask) {
    ecl = QR_ECC[ecl] ? ecl : "M";
    var bytes = utf8Bytes(String(text == null ? "" : text));
    var ver;
    var bits = [];
    function push(val, len) {
      for (var i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1);
    }
    for (ver = 1; ver <= 40; ver++) {
      var ccBits = ver <= 9 ? 8 : 16;
      var need = 4 + ccBits + bytes.length * 8;
      if (need <= qrDataCodewords(ver, ecl) * 8) break;
    }
    if (ver > 40) throw new ZPError("QR data too long");
    push(4, 4);
    push(bytes.length, ver <= 9 ? 8 : 16);
    for (var bi = 0; bi < bytes.length; bi++) push(bytes[bi], 8);
    var cap = qrDataCodewords(ver, ecl) * 8;
    push(0, Math.min(4, cap - bits.length));
    push(0, (8 - (bits.length % 8)) % 8);
    for (var padByte = 0xec; bits.length < cap; padByte ^= 0xec ^ 0x11) push(padByte, 8);
    var data = [];
    for (var di = 0; di < bits.length; di += 8) {
      var v = 0;
      for (var k = 0; k < 8; k++) v = (v << 1) | bits[di + k];
      data.push(v);
    }
    // error correction + interleave
    var nb = QR_ECC[ecl].nb[ver];
    var eccLen = QR_ECC[ecl].cw[ver];
    var raw = Math.floor(qrRawModules(ver) / 8);
    var numShort = nb - (raw % nb);
    var shortLen = Math.floor(raw / nb);
    var blocks = [];
    var div = rsDivisor(eccLen);
    for (var b = 0, off = 0; b < nb; b++) {
      var dat = data.slice(off, off + shortLen - eccLen + (b < numShort ? 0 : 1));
      off += dat.length;
      var ecc = rsRemainder(dat, div);
      if (b < numShort) dat.push(0);
      blocks.push(dat.concat(ecc));
    }
    var all = [];
    for (var ci = 0; ci < blocks[0].length; ci++) {
      for (var bj = 0; bj < blocks.length; bj++) {
        if (ci !== shortLen - eccLen || bj >= numShort) all.push(blocks[bj][ci]);
      }
    }
    // modules
    var size = ver * 4 + 17;
    var mods = [];
    var fn = [];
    for (var y = 0; y < size; y++) {
      mods.push([]);
      fn.push([]);
      for (var x = 0; x < size; x++) {
        mods[y].push(false);
        fn[y].push(false);
      }
    }
    function setF(x, y, dark) {
      mods[y][x] = dark;
      fn[y][x] = true;
    }
    var i;
    for (i = 0; i < size; i++) {
      setF(6, i, i % 2 === 0);
      setF(i, 6, i % 2 === 0);
    }
    function finder(cx, cy) {
      for (var dy = -4; dy <= 4; dy++)
        for (var dx = -4; dx <= 4; dx++) {
          var d = Math.max(Math.abs(dx), Math.abs(dy));
          var xx = cx + dx;
          var yy = cy + dy;
          if (xx >= 0 && xx < size && yy >= 0 && yy < size) setF(xx, yy, d !== 2 && d !== 4);
        }
    }
    finder(3, 3);
    finder(size - 4, 3);
    finder(3, size - 4);
    var align = [];
    if (ver > 1) {
      var na = Math.floor(ver / 7) + 2;
      var step = ver === 32 ? 26 : Math.ceil((ver * 4 + 4) / (na * 2 - 2)) * 2;
      align = [6];
      for (var pos = size - 7; align.length < na; pos -= step) align.splice(1, 0, pos);
    }
    for (i = 0; i < align.length; i++)
      for (var j = 0; j < align.length; j++) {
        if ((i === 0 && j === 0) || (i === 0 && j === align.length - 1) || (i === align.length - 1 && j === 0)) continue;
        for (var ay = -2; ay <= 2; ay++) for (var ax = -2; ax <= 2; ax++) setF(align[i] + ax, align[j] + ay, Math.max(Math.abs(ax), Math.abs(ay)) !== 1);
      }
    function formatBits(mask) {
      var d = (QR_ECC[ecl].f << 3) | mask;
      var rem = d;
      for (var q = 0; q < 10; q++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
      var fb = ((d << 10) | rem) ^ 0x5412;
      var q2;
      for (q2 = 0; q2 <= 5; q2++) setF(8, q2, ((fb >>> q2) & 1) !== 0);
      setF(8, 7, ((fb >>> 6) & 1) !== 0);
      setF(8, 8, ((fb >>> 7) & 1) !== 0);
      setF(7, 8, ((fb >>> 8) & 1) !== 0);
      for (q2 = 9; q2 < 15; q2++) setF(14 - q2, 8, ((fb >>> q2) & 1) !== 0);
      for (q2 = 0; q2 < 8; q2++) setF(size - 1 - q2, 8, ((fb >>> q2) & 1) !== 0);
      for (q2 = 8; q2 < 15; q2++) setF(8, size - 15 + q2, ((fb >>> q2) & 1) !== 0);
      setF(8, size - 8, true);
    }
    formatBits(0);
    if (ver >= 7) {
      var vr = ver;
      for (i = 0; i < 12; i++) vr = (vr << 1) ^ ((vr >>> 11) * 0x1f25);
      var vb = (ver << 12) | vr;
      for (i = 0; i < 18; i++) {
        var bit = ((vb >>> i) & 1) !== 0;
        var aa = size - 11 + (i % 3);
        var bb = Math.floor(i / 3);
        setF(aa, bb, bit);
        setF(bb, aa, bit);
      }
    }
    // data
    var bitIdx = 0;
    for (var right = size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (var vert = 0; vert < size; vert++) {
        for (var jj = 0; jj < 2; jj++) {
          var xx2 = right - jj;
          var up = ((right + 1) & 2) === 0;
          var yy2 = up ? size - 1 - vert : vert;
          if (!fn[yy2][xx2] && bitIdx < all.length * 8) {
            mods[yy2][xx2] = ((all[bitIdx >>> 3] >>> (7 - (bitIdx & 7))) & 1) !== 0;
            bitIdx++;
          }
        }
      }
    }
    function applyMask(m) {
      for (var yy = 0; yy < size; yy++)
        for (var xx = 0; xx < size; xx++) {
          var inv;
          switch (m) {
            case 0: inv = (xx + yy) % 2 === 0; break;
            case 1: inv = yy % 2 === 0; break;
            case 2: inv = xx % 3 === 0; break;
            case 3: inv = (xx + yy) % 3 === 0; break;
            case 4: inv = (Math.floor(xx / 3) + Math.floor(yy / 2)) % 2 === 0; break;
            case 5: inv = ((xx * yy) % 2) + ((xx * yy) % 3) === 0; break;
            case 6: inv = (((xx * yy) % 2) + ((xx * yy) % 3)) % 2 === 0; break;
            default: inv = (((xx + yy) % 2) + ((xx * yy) % 3)) % 2 === 0;
          }
          if (!fn[yy][xx] && inv) mods[yy][xx] = !mods[yy][xx];
        }
    }
    function penalty() {
      var p = 0;
      var yy;
      var xx;
      var run;
      var c;
      for (yy = 0; yy < size; yy++) {
        run = 1;
        for (xx = 1; xx <= size; xx++) {
          if (xx < size && mods[yy][xx] === mods[yy][xx - 1]) run++;
          else {
            if (run >= 5) p += run - 2;
            run = 1;
          }
        }
      }
      for (xx = 0; xx < size; xx++) {
        run = 1;
        for (yy = 1; yy <= size; yy++) {
          if (yy < size && mods[yy][xx] === mods[yy - 1][xx]) run++;
          else {
            if (run >= 5) p += run - 2;
            run = 1;
          }
        }
      }
      for (yy = 0; yy < size - 1; yy++)
        for (xx = 0; xx < size - 1; xx++) {
          c = mods[yy][xx];
          if (c === mods[yy][xx + 1] && c === mods[yy + 1][xx] && c === mods[yy + 1][xx + 1]) p += 3;
        }
      function pat(get) {
        var s = 0;
        for (var t = 0; t + 10 < size; t++) {
          var a1 = get(t), a2 = get(t + 1), a3 = get(t + 2), a4 = get(t + 3), a5 = get(t + 4), a6 = get(t + 5), a7 = get(t + 6);
          if (a1 && !a2 && a3 && a4 && a5 && !a6 && a7) {
            var before = !get(t - 1) && !get(t - 2) && !get(t - 3) && !get(t - 4);
            var after = !get(t + 7) && !get(t + 8) && !get(t + 9) && !get(t + 10);
            if (before || after) s += 40;
          }
        }
        return s;
      }
      for (yy = 0; yy < size; yy++) {
        (function (row) {
          p += pat(function (t) { return t >= 0 && t < size ? mods[row][t] : false; });
        })(yy);
      }
      for (xx = 0; xx < size; xx++) {
        (function (col) {
          p += pat(function (t) { return t >= 0 && t < size ? mods[t][col] : false; });
        })(xx);
      }
      var dark = 0;
      for (yy = 0; yy < size; yy++) for (xx = 0; xx < size; xx++) if (mods[yy][xx]) dark++;
      var total = size * size;
      var kk = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
      p += Math.max(0, kk) * 10;
      return p;
    }
    var best = forceMask;
    if (best === undefined || best === null) {
      var minP = Infinity;
      for (var m = 0; m < 8; m++) {
        applyMask(m);
        formatBits(m);
        var pp = penalty();
        if (pp < minP) {
          minP = pp;
          best = m;
        }
        applyMask(m);
      }
    }
    applyMask(best);
    formatBits(best);
    return { size: size, modules: mods, version: ver, mask: best };
  }
  function qrSvg(text, opts) {
    opts = opts || {};
    var q = qrEncode(text, opts.ecl || "M");
    var quiet = opts.quiet === undefined ? 1 : opts.quiet;
    var n = q.size + quiet * 2;
    var d = "";
    for (var y = 0; y < q.size; y++)
      for (var x = 0; x < q.size; x++) if (q.modules[y][x]) d += "M" + (x + quiet) + "," + (y + quiet) + "h1v1h-1z";
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + n + " " + n + '" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" shape-rendering="crispEdges"><path fill="' + cssVal(opts.color || "#000") + '" d="' + d + '"/></svg>';
  }

  /* ------------------------------------------------------------------ *
   * Code 128 barcode (auto switches B / C)
   * ------------------------------------------------------------------ */
  var C128 = [11011001100, 11001101100, 11001100110, 10010011000, 10010001100, 10001001100, 10011001000, 10011000100, 10001100100, 11001001000, 11001000100, 11000100100, 10110011100, 10011011100, 10011001110, 10111001100, 10011101100, 10011100110, 11001110010, 11001011100, 11001001110, 11011100100, 11001110100, 11101101110, 11101001100, 11100101100, 11100100110, 11101100100, 11100110100, 11100110010, 11011011000, 11011000110, 11000110110, 10100011000, 10001011000, 10001000110, 10110001000, 10001101000, 10001100010, 11010001000, 11000101000, 11000100010, 10110111000, 10110001110, 10001101110, 10111011000, 10111000110, 10001110110, 11101110110, 11010001110, 11000101110, 11011101000, 11011100010, 11011101110, 11101011000, 11101000110, 11100010110, 11101101000, 11101100010, 11100011010, 11101111010, 11001000010, 11110001010, 10100110000, 10100001100, 10010110000, 10010000110, 10000101100, 10000100110, 10110010000, 10110000100, 10011010000, 10011000010, 10000110100, 10000110010, 11000010010, 11001010000, 11110111010, 11000010100, 10001111010, 10100111100, 10010111100, 10010011110, 10111100100, 10011110100, 10011110010, 11110100100, 11110010100, 11110010010, 11011011110, 11011110110, 11110110110, 10101111000, 10100011110, 10001011110, 10111101000, 10111100010, 11110101000, 11110100010, 10111011110, 10111101110, 11101011110, 11110101110, 11010000100, 11010010000, 11010011100, 1100011101011];
  function code128Codes(text) {
    var s = String(text == null ? "" : text).replace(/[^\x20-\x7E]/g, "");
    var codes = [];
    var i = 0;
    var set = null;
    function digitsRun(at) {
      var k = at;
      while (k < s.length && /[0-9]/.test(s.charAt(k))) k++;
      return k - at;
    }
    while (i < s.length) {
      var run = digitsRun(i);
      var useC = run >= 4 && (set === "C" || run >= (i === 0 || i + run === s.length ? 4 : 6));
      if (useC) {
        if (set !== "C") {
          codes.push(set === null ? 105 : 99);
          set = "C";
        }
        var pairs = Math.floor(run / 2);
        for (var p = 0; p < pairs; p++) {
          codes.push(parseInt(s.substr(i, 2), 10));
          i += 2;
        }
        continue;
      }
      if (set !== "B") {
        codes.push(set === null ? 104 : 100);
        set = "B";
      }
      codes.push(s.charCodeAt(i) - 32);
      i++;
    }
    if (!codes.length) codes.push(104);
    var sum = codes[0];
    for (i = 1; i < codes.length; i++) sum += codes[i] * i;
    codes.push(sum % 103);
    codes.push(106);
    return codes;
  }
  function barcodeSvg(text, opts) {
    opts = opts || {};
    var codes = code128Codes(text);
    var bits = "";
    for (var i = 0; i < codes.length; i++) bits += String(C128[codes[i]]);
    var quiet = 10;
    var w = bits.length + quiet * 2;
    var d = "";
    var x = 0;
    while (x < bits.length) {
      if (bits.charAt(x) === "1") {
        var st = x;
        while (x < bits.length && bits.charAt(x) === "1") x++;
        d += "M" + (st + quiet) + ",0h" + (x - st) + "v100h-" + (x - st) + "z";
      } else x++;
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + w + ' 100" width="100%" height="100%" preserveAspectRatio="none" shape-rendering="crispEdges"><path fill="' + cssVal(opts.color || "#000") + '" d="' + d + '"/></svg>';
  }

  /* ------------------------------------------------------------------ *
   * Paper
   * ------------------------------------------------------------------ */
  var PAPERS = {
    A3: { width: 297, height: 420 },
    A4: { width: 210, height: 297 },
    A5: { width: 148, height: 210 },
    A6: { width: 105, height: 148 },
    LETTER: { width: 215.9, height: 279.4 },
    LEGAL: { width: 215.9, height: 355.6 },
    T58: { width: 58, height: 0, continuous: true },
    T57: { width: 57, height: 0, continuous: true },
    T76: { width: 76, height: 0, continuous: true },
    T80: { width: 80, height: 0, continuous: true },
    T104: { width: 104, height: 0, continuous: true },
    T112: { width: 112, height: 0, continuous: true }
  };
  function paperSize(t) {
    var p = (t && t.paper) || {};
    var preset = PAPERS[String(p.preset || "A4").toUpperCase()];
    var w = num(p.width, preset ? preset.width : 210);
    var h = num(p.height, preset ? preset.height : 297);
    var continuous = p.continuous === undefined ? !!(preset && preset.continuous) : !!p.continuous;
    if (!continuous && p.orientation === "landscape" && h > w) {
      var tmp = w;
      w = h;
      h = tmp;
    }
    var m = p.margins || {};
    var margins = { top: num(m.top, 5), right: num(m.right, 5), bottom: num(m.bottom, 5), left: num(m.left, 5) };
    return {
      width: w,
      height: continuous ? 0 : h,
      continuous: continuous,
      margins: margins,
      contentWidth: Math.max(10, w - margins.left - margins.right),
      contentHeight: continuous ? 0 : Math.max(10, h - margins.top - margins.bottom)
    };
  }

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */
  var FONT_STACKS = {
    Arial: "Arial, Helvetica, sans-serif",
    Helvetica: "Helvetica, Arial, sans-serif",
    Verdana: "Verdana, Geneva, sans-serif",
    Tahoma: "Tahoma, Verdana, sans-serif",
    Calibri: "Calibri, Carlito, Arial, sans-serif",
    "Segoe UI": "'Segoe UI', Roboto, Arial, sans-serif",
    Roboto: "Roboto, 'Segoe UI', Arial, sans-serif",
    "Times New Roman": "'Times New Roman', Times, serif",
    Georgia: "Georgia, 'Times New Roman', serif",
    "Courier New": "'Courier New', Courier, monospace",
    Consolas: "Consolas, 'Courier New', monospace",
    Monospace: "'Lucida Console', 'Courier New', monospace"
  };
  function fontStack(f) {
    return FONT_STACKS[f] || (f ? cssVal(f) + ", Arial, sans-serif" : "");
  }

  function borderCss(st, prefix) {
    var w = num(st.borderWidth, 0);
    if (!w) return "";
    var color = cssVal(st.borderColor || "#000");
    var style = cssVal(st.borderStyle || "solid");
    var sides = st.borders || "all";
    var spec = w + "pt " + style + " " + color;
    if (sides === "all" || sides === true) return (prefix || "") + "border:" + spec + ";";
    var out = "";
    var map = { t: "top", r: "right", b: "bottom", l: "left" };
    for (var k in map) {
      if (hasOwn.call(map, k) && String(sides).indexOf(k) >= 0) out += "border-" + map[k] + ":" + spec + ";";
    }
    return out;
  }

  // Text styling shared by elements and table cells.
  function textCss(st) {
    st = st || {};
    var c = "";
    if (st.fontFamily) c += "font-family:" + fontStack(st.fontFamily) + ";";
    if (st.fontSize) c += "font-size:" + num(st.fontSize, 9) + "pt;";
    if (st.bold !== undefined) c += "font-weight:" + (st.bold ? "700" : "400") + ";";
    if (st.italic) c += "font-style:italic;";
    var deco = [];
    if (st.underline) deco.push("underline");
    if (st.strike) deco.push("line-through");
    if (deco.length) c += "text-decoration:" + deco.join(" ") + ";";
    if (st.color) c += "color:" + cssVal(st.color) + ";";
    if (st.align) c += "text-align:" + cssVal(st.align) + ";";
    if (st.lineHeight) c += "line-height:" + num(st.lineHeight, 1.2) + ";";
    if (st.letterSpacing) c += "letter-spacing:" + num(st.letterSpacing, 0) + "pt;";
    if (st.uppercase) c += "text-transform:uppercase;";
    return c;
  }

  function elementBoxCss(el, noPosition) {
    var st = el.style || {};
    var c = noPosition ? "position:absolute;left:0;top:0;right:0;bottom:0;" : "position:absolute;left:" + mm(el.x) + ";top:" + mm(el.y) + ";width:" + mm(el.w) + ";height:" + mm(el.h) + ";";
    if (el.type === "line") return c;
    // a table's style describes its cells, not the box around it
    if (el.type === "table") return c + (el.rotate ? "transform:rotate(" + num(el.rotate, 0) + "deg);" : "");
    c += textCss(st);
    if (st.bg) c += "background:" + cssVal(st.bg) + ";";
    c += borderCss(st);
    if (st.radius) c += "border-radius:" + mm(st.radius) + ";";
    if (st.padding !== undefined && st.padding !== "") c += "padding:" + mm(st.padding) + ";";
    if (st.opacity !== undefined && st.opacity !== "" && num(st.opacity, 1) < 1) c += "opacity:" + num(st.opacity, 1) + ";";
    if (st.clip) c += "overflow:hidden;";
    if (el.rotate) c += "transform:rotate(" + num(el.rotate, 0) + "deg);transform-origin:center;";
    var va = st.valign || "top";
    c += "display:flex;flex-direction:column;justify-content:" + (va === "middle" ? "center" : va === "bottom" ? "flex-end" : "flex-start") + ";";
    return c;
  }

  // `markup` enables **bold** and __underline__ inside static text.
  function textBlock(text, st, markup) {
    st = st || {};
    var ws = st.wrap === false ? "white-space:pre;overflow:hidden;text-overflow:clip;" : "white-space:pre-wrap;word-break:break-word;";
    var h = escHtml(text);
    if (markup) h = h.replace(/\*\*([\s\S]+?)\*\*/g, "<b>$1</b>").replace(/__([\s\S]+?)__/g, "<u>$1</u>");
    return '<div class="zp-tx" style="' + ws + '">' + h + "</div>";
  }

  // Returns the inner HTML of an element and collects any errors.
  Context.prototype.elementContent = function (el, scope, errors) {
    var st = el.style || {};
    var r;
    switch (el.type) {
      case "text":
        return textBlock(this.interpolate(el.content, scope, errors), st, true);
      case "field":
        r = this.evalSafe(el.expr, scope);
        if (r.error) {
          errors.push(r.error);
          return textBlock("#ERR", st);
        }
        var val = applyFormat(r.value, el.format);
        var s = toStr(val);
        if (s !== "" || el.showLabelWhenEmpty) s = (el.prefix || "") + s + (el.suffix || "");
        return textBlock(s, st);
      case "image":
        var src = el.src || "";
        if (el.srcExpr) {
          r = this.evalSafe(el.srcExpr, scope);
          if (r.error) errors.push(r.error);
          src = toStr(r.value) || src;
        }
        if (!src) return el.placeholder ? '<div class="zp-img-ph">' + escHtml(el.placeholder) + "</div>" : "";
        if (!/^(https?:|data:image\/|blob:|\/)/i.test(src) && !/^[\w\-./]+$/.test(src)) return "";
        return '<img src="' + escHtml(src) + '" alt="" style="width:100%;height:100%;object-fit:' + cssVal(el.fit || "contain") + ';display:block"/>';
      case "line":
        var lw = num(st.lineWidth, 0.75);
        var lc = cssVal(st.color || "#000");
        var ls = cssVal(st.lineStyle || "solid");
        if (el.orientation === "v") return '<div style="position:absolute;left:50%;top:0;bottom:0;border-left:' + lw + "pt " + ls + " " + lc + ';transform:translateX(-50%)"></div>';
        return '<div style="position:absolute;left:0;right:0;top:50%;border-top:' + lw + "pt " + ls + " " + lc + ';transform:translateY(-50%)"></div>';
      case "rect":
        return "";
      case "qr":
        r = this.evalSafe(el.expr, scope);
        if (r.error) {
          errors.push(r.error);
          return textBlock("#ERR", st);
        }
        var qv = toStr(r.value);
        if (!qv) return "";
        try {
          return qrSvg(qv, { ecl: el.ecl || "M", color: st.color, quiet: el.quiet === undefined ? 1 : el.quiet });
        } catch (e) {
          errors.push(e.message);
          return textBlock("#QR", st);
        }
      case "barcode":
        r = this.evalSafe(el.expr, scope);
        if (r.error) {
          errors.push(r.error);
          return textBlock("#ERR", st);
        }
        var bv = toStr(r.value);
        if (!bv) return "";
        var showText = el.showText !== false;
        return '<div style="flex:1;min-height:0">' + barcodeSvg(bv, { color: st.color }) + "</div>" + (showText ? '<div style="text-align:center;white-space:nowrap;line-height:1.1">' + escHtml(bv) + "</div>" : "");
      case "table":
        return this.renderTable(el, errors);
    }
    return "";
  };

  Context.prototype.isVisible = function (obj, scope, errors) {
    if (!obj || !obj.visibleIf || !String(obj.visibleIf).trim()) return true;
    var r = this.evalSafe(obj.visibleIf, scope);
    if (r.error) {
      if (errors) errors.push(r.error);
      return true;
    }
    return truthy(r.value);
  };

  Context.prototype.renderElement = function (el, opts) {
    opts = opts || {};
    var errors = [];
    var scope = this.rootScope();
    if (!opts.ignoreVisibility && !this.isVisible(el, scope, errors)) return { html: "", errors: errors, hidden: true };
    var inner = this.elementContent(el, scope, errors);
    var html = '<div class="zp-el zp-' + el.type + '" style="' + elementBoxCss(el, opts.noPosition) + '">' + inner + "</div>";
    return { html: html, errors: errors };
  };

  function cellBorderCss(gs, isHeader, isFirstRow) {
    var w = num(gs.borderWidth, 0.5);
    var color = cssVal(gs.borderColor || "#000");
    var spec = w + "pt solid " + color;
    switch (gs.grid || "all") {
      case "all":
        return "border:" + spec + ";";
      case "rows":
        return "border-bottom:" + spec + ";" + (isHeader ? "border-top:" + spec + ";" : "");
      case "columns":
        return "border-left:" + spec + ";border-right:" + spec + ";" + (isHeader ? "border-bottom:" + spec + ";border-top:" + spec + ";" : "");
      case "header":
        return isHeader ? "border-bottom:" + spec + ";border-top:" + spec + ";" : "";
      default:
        return "";
    }
  }

  Context.prototype.visibleColumns = function (el, errors) {
    var cols = el.columns || [];
    var out = [];
    var scope = this.rootScope();
    var total = 0;
    var i;
    for (i = 0; i < cols.length; i++) {
      if (!this.isVisible(cols[i], scope, errors)) continue;
      out.push(cols[i]);
      total += Math.max(0.1, num(cols[i].width, 10));
    }
    var res = [];
    for (i = 0; i < out.length; i++) res.push({ col: out[i], pct: (Math.max(0.1, num(out[i].width, 10)) / (total || 1)) * 100 });
    return res;
  };

  Context.prototype.renderTable = function (el, errors) {
    var info = this.tables[el.id] || this.registerTable(el);
    var ts = el.style || {};
    var rows = this.tableRows(info);
    var cols = this.visibleColumns(el, errors);
    var root = this.rootScope();
    var pad = mm(ts.cellPadding === undefined ? 1 : ts.cellPadding);
    var html = '<table class="zp-tbl" data-zp-table="' + escHtml(el.id) + '" style="width:100%;' + textCss(ts) + (ts.grid === "outer" || ts.outerBorder ? "border:" + num(ts.borderWidth, 0.5) + "pt solid " + cssVal(ts.borderColor || "#000") + ";" : "") + '">';
    html += "<colgroup>";
    var i;
    var j;
    for (i = 0; i < cols.length; i++) html += '<col style="width:' + round(cols[i].pct, 4) + '%">';
    html += "</colgroup>";
    if (el.showHeader !== false) {
      html += '<thead style="display:' + (el.repeatHeader === false ? "table-row-group" : "table-header-group") + '"><tr>';
      var hs = el.headerStyle || {};
      for (i = 0; i < cols.length; i++) {
        var c = cols[i].col;
        var hcss = "padding:" + pad + ";" + cellBorderCss(ts, true) + textCss(extend({ bold: true, align: c.align || "left" }, hs)) + (hs.bg ? "background:" + cssVal(hs.bg) + ";" : "") + (c.headerAlign ? "text-align:" + cssVal(c.headerAlign) + ";" : "") + "vertical-align:" + cssVal(hs.valign || "middle") + ";";
        html += '<th data-zp-col="' + escHtml(c.id || "") + '" style="' + hcss + '">' + escHtml(this.interpolate(c.header, root, errors)) + "</th>";
      }
      html += "</tr></thead>";
    }
    html += "<tbody>";
    var rowH = num(ts.rowHeight, 0);
    var zebra = ts.zebra ? cssVal(ts.zebra) : "";
    for (var r = 0; r < rows.length; r++) {
      var sc = this.rowScope(info, r);
      var rowCss = zebra && r % 2 === 1 ? "background:" + zebra + ";" : "";
      if (el.rowStyleIf && el.rowStyleIf.expr && truthy(this.evalSafe(el.rowStyleIf.expr, sc).value)) {
        var rsi = el.rowStyleIf.style || {};
        rowCss += textCss(rsi) + (rsi.bg ? "background:" + cssVal(rsi.bg) + ";" : "");
      }
      html += '<tr style="' + rowCss + '">';
      for (j = 0; j < cols.length; j++) {
        var col = cols[j].col;
        var out;
        try {
          var v = col.key ? this.colValue(info, r, col.key) : this.evaluate(parse(col.expr), sc);
          out = toStr(applyFormat(v, col.format));
        } catch (e) {
          errors.push((col.header || col.key || "Column") + ": " + (e && e.message));
          out = "#ERR";
        }
        var cs = col.style || {};
        var ccss = "padding:" + pad + ";" + cellBorderCss(ts, false) + textCss(cs) + "text-align:" + cssVal(col.align || "left") + ";vertical-align:" + cssVal(ts.valign || "top") + ";" + (col.nowrap ? "white-space:nowrap;overflow:hidden;" : "white-space:pre-wrap;word-break:break-word;") + (rowH ? "height:" + mm(rowH) + ";" : "") + (cs.bg ? "background:" + cssVal(cs.bg) + ";" : "");
        html += '<td style="' + ccss + '">' + escHtml(out) + "</td>";
      }
      html += "</tr>";
    }
    var minRows = Math.floor(num(el.minRows, 0));
    for (r = rows.length; r < minRows; r++) {
      html += "<tr>";
      for (j = 0; j < cols.length; j++) html += '<td style="padding:' + pad + ";" + cellBorderCss(ts, false) + "height:" + mm(rowH || 5) + ';">&nbsp;</td>';
      html += "</tr>";
    }
    if (el.showFooter) {
      var fs = el.footerStyle || {};
      html += '<tr class="zp-tfoot">';
      for (j = 0; j < cols.length; j++) {
        var fc = cols[j].col;
        var ft = "";
        if (fc.footer && String(fc.footer).trim()) {
          var fsc = { ctx: this, row: null, rowIndex: -1, table: info, bind: null };
          var fr = this.evalSafe(fc.footer, fsc);
          if (fr.error) {
            errors.push((fc.header || "Footer") + " total: " + fr.error);
            ft = "#ERR";
          } else ft = toStr(applyFormat(fr.value, fc.footerFormat || fc.format));
        } else if (fc.footerText) ft = this.interpolate(fc.footerText, root, errors);
        var fcss = "padding:" + pad + ";" + cellBorderCss(ts, true) + textCss(extend({ bold: true }, fs)) + "text-align:" + cssVal(fc.align || "left") + ";" + (fs.bg ? "background:" + cssVal(fs.bg) + ";" : "");
        html += '<td style="' + fcss + '">' + escHtml(ft) + "</td>";
      }
      html += "</tr>";
    }
    html += "</tbody></table>";
    return html;
  };

  Context.prototype.renderBand = function (band, errors) {
    var root = this.rootScope();
    if (!this.isVisible(band, root, errors)) return "";
    var els = band.elements || [];
    var css = "position:relative;width:100%;";
    if (band.bg) css += "background:" + cssVal(band.bg) + ";";
    if (band.pageBreakBefore) css += "break-before:page;page-break-before:always;";
    var html = "";
    if (band.type === "table") {
      css += "padding-top:" + mm(band.paddingTop || 0) + ";padding-bottom:" + mm(band.paddingBottom || 0) + ";";
      for (var i = 0; i < els.length; i++) {
        var t = els[i];
        if (!this.isVisible(t, root, errors)) continue;
        html += '<div class="zp-tblwrap" style="margin-left:' + mm(t.x || 0) + ";width:" + mm(t.w || 10) + ';">' + this.renderTable(t, errors) + "</div>";
      }
      return '<div class="zp-band zp-band-table" style="' + css + '">' + html + "</div>";
    }
    css += "height:" + mm(band.height || 10) + ";";
    if (band.keepTogether !== false) css += "break-inside:avoid;page-break-inside:avoid;";
    for (var k = 0; k < els.length; k++) {
      var r = this.renderElement(els[k]);
      for (var e = 0; e < r.errors.length; e++) errors.push(r.errors[e]);
      html += r.html;
    }
    return '<div class="zp-band" style="' + css + '">' + html + "</div>";
  };

  function baseCss() {
    return (
      ".zp-root{position:relative;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact;line-height:1.2;text-align:left}" +
      ".zp-root *{box-sizing:border-box}" +
      ".zp-el{overflow:visible}" +
      ".zp-tx{max-width:100%}" +
      ".zp-tbl{border-collapse:collapse;table-layout:fixed}" +
      ".zp-tbl tr{break-inside:avoid;page-break-inside:avoid}" +
      ".zp-tbl td,.zp-tbl th{overflow:hidden}" +
      ".zp-wrap{width:100%;border-collapse:collapse;border-spacing:0}" +
      ".zp-wrap>thead>tr>td,.zp-wrap>tfoot>tr>td,.zp-wrap>tbody>tr>td{padding:0;vertical-align:top}" +
      ".zp-img-ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;border:1px dashed #9ca3af;color:#6b7280;font-size:7pt}"
    );
  }

  function pageCss(template, heightOverride) {
    var ps = paperSize(template);
    var h = heightOverride || ps.height || 297;
    var m = ps.margins;
    var d = template.defaults || {};
    var c = "@page{size:" + round(ps.width, 3) + "mm " + round(h, 3) + "mm;margin:" + m.top + "mm " + m.right + "mm " + m.bottom + "mm " + m.left + "mm}";
    c += "html,body{margin:0;padding:0;background:#fff}";
    c += ".zp-root{width:" + round(ps.contentWidth, 3) + "mm;font-family:" + fontStack(d.fontFamily || "Arial") + ";font-size:" + num(d.fontSize, 9) + "pt;color:" + cssVal(d.color || "#000") + "}";
    c += "@media screen{body{background:#e5e7eb}.zp-sheet{background:#fff;width:" + round(ps.width, 3) + "mm;" + (ps.continuous ? "" : "min-height:" + round(ps.height, 3) + "mm;") + "margin:6mm auto;padding:" + m.top + "mm " + m.right + "mm " + m.bottom + "mm " + m.left + "mm;box-shadow:0 1px 6px rgba(0,0,0,.25)}}";
    c += "@media print{.zp-sheet{width:auto;padding:0;margin:0;box-shadow:none}}";
    return c;
  }

  Context.prototype.renderBody = function () {
    var bands = this.template.bands || [];
    var errors = [];
    var top = "";
    var bottom = "";
    var body = "";
    var ps = paperSize(this.template);
    for (var i = 0; i < bands.length; i++) {
      var b = bands[i];
      var h = this.renderBand(b, errors);
      if (!ps.continuous && b.repeat === "top") top += h;
      else if (!ps.continuous && b.repeat === "bottom") bottom += h;
      else body += h;
    }
    var html;
    if (top || bottom) {
      html = '<table class="zp-wrap">' + (top ? "<thead><tr><td>" + top + "</td></tr></thead>" : "") + (bottom ? "<tfoot><tr><td>" + bottom + "</td></tr></tfoot>" : "") + "<tbody><tr><td>" + body + "</td></tr></tbody></table>";
    } else html = body;
    var warnings = [];
    for (var w in this.warnings) if (hasOwn.call(this.warnings, w)) warnings.push(w);
    return { html: '<div class="zp-root">' + html + "</div>", errors: errors, warnings: warnings };
  };

  function createContext(template, data, opts) {
    return new Context(template, data, opts);
  }

  function renderDocument(template, data, opts) {
    var ctx = new Context(template, data, opts);
    var out = ctx.renderBody();
    out.css = baseCss() + pageCss(template);
    return out;
  }

  // Used by the printed page: renders into the current document.
  function mount(template, data, doc) {
    doc = doc || document;
    var out = renderDocument(template, data);
    var style = doc.getElementById("zp-style") || doc.createElement("style");
    style.id = "zp-style";
    style.textContent = out.css;
    if (!style.parentNode) (doc.head || doc.getElementsByTagName("head")[0] || doc.documentElement).appendChild(style);
    var host = doc.getElementById("zp-mount");
    if (!host) {
      host = doc.createElement("div");
      host.id = "zp-mount";
      doc.body.appendChild(host);
    }
    host.innerHTML = '<div class="zp-sheet">' + out.html + "</div>";
    var ps = paperSize(template);
    if (ps.continuous) {
      // Roll paper: size the page to the rendered content so the printer cuts right after it.
      var fit = function () {
        var root = host.querySelector(".zp-root");
        if (!root) return;
        var hmm = root.getBoundingClientRect().height / MM_TO_PX + ps.margins.top + ps.margins.bottom + 2;
        var ps2 = doc.getElementById("zp-page-size") || doc.createElement("style");
        ps2.id = "zp-page-size";
        ps2.textContent = "@page{size:" + round(ps.width, 3) + "mm " + Math.ceil(hmm) + "mm}";
        if (!ps2.parentNode) (doc.head || doc.documentElement).appendChild(ps2);
      };
      fit();
      var imgs = host.getElementsByTagName("img");
      for (var i = 0; i < imgs.length; i++) imgs[i].onload = fit;
    }
    return out;
  }

  return {
    version: VERSION,
    parse: parse,
    tokenize: tokenize,
    createContext: createContext,
    renderDocument: renderDocument,
    mount: mount,
    applyFormat: applyFormat,
    formatDate: formatDate,
    formatNumber: formatNumber,
    amountInWords: amountInWords,
    toStr: toStr,
    toNum: toNum,
    paperSize: paperSize,
    papers: PAPERS,
    fonts: FONT_STACKS,
    baseCss: baseCss,
    pageCss: pageCss,
    elementBoxCss: elementBoxCss,
    parseTemplateText: parseTemplateText,
    qrEncode: qrEncode,
    qrSvg: qrSvg,
    barcodeSvg: barcodeSvg,
    code128Codes: code128Codes,
    normalizeData: normalizeData,
    MM_TO_PX: MM_TO_PX
  };
}
